/**
 * v3 timeline JSON: `timeline.events[]` (replaces legacy `timeline.stages[]`).
 */

import { parseLocalDateString } from './localDate';

/**
 * Legacy → canonical: `submitted` → `initial_filing`; `continuing` → `pending` (case status / process events).
 */
export function normalizeTimelineEventType(et) {
  const t = String(et || '').toLowerCase();
  if (t === 'submitted') return 'initial_filing';
  if (t === 'continuing') return 'pending';
  return t;
}

/** @returns {Array<object>} */
export function getTimelineEventList(timeline) {
  if (!timeline) return [];
  if (Array.isArray(timeline.events)) {
    return timeline.events.map((e) => ({
      ...e,
      event_type: normalizeTimelineEventType(e.event_type),
    }));
  }
  if (Array.isArray(timeline.stages)) {
    return timeline.stages.map((s) => ({
      date: s.date,
      event_type: normalizeTimelineEventType(String(s.status || 'pending').toLowerCase()),
      label: s.stage_name || s.status || 'Event',
      body: null,
      vote: null,
      notes: null,
    }));
  }
  return [];
}

export function sortEventsByDate(events) {
  return [...events].sort((a, b) => {
    const da = parseLocalDateString(a.date)?.getTime() ?? 0;
    const db = parseLocalDateString(b.date)?.getTime() ?? 0;
    return da - db;
  });
}

/** Event types that render at 30px (milestone bubbles with icons). */
export const MILESTONE_EVENT_TYPES = new Set([
  'initial_filing',
  'approved', 'denied', 'withdrawn',
  'recommendation_approval', 'recommendation_denial',
]);

/** All event types that get an icon bubble (same as milestones). */
export const ICON_EVENT_TYPES = MILESTONE_EVENT_TYPES;

/** Bubble diameter derived from event_type (tier field is no longer used). */
export function eventBubbleDiameter(event) {
  const et = normalizeTimelineEventType(event?.event_type);
  if (MILESTONE_EVENT_TYPES.has(et)) return 30;
  return 10;
}

/**
 * Filter events by milestones-only toggle.
 * When milestonesOnly is true, keep only milestone + initial_filing events.
 */
export function filterEventsByMode(events, milestonesOnly) {
  if (!milestonesOnly) return events;
  return events.filter((e) => {
    const et = normalizeTimelineEventType(e?.event_type);
    return ICON_EVENT_TYPES.has(et);
  });
}

/** Icon font sizes (px) per event_type. */
export function markerIconFontSizePx(eventType) {
  const et = normalizeTimelineEventType(eventType);
  if (et === 'recommendation_approval' || et === 'recommendation_denial') return 16;
  return 18;
}

export function capitalizeWords(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Tooltip line 1: prefer `label` when present (v3 narrative line);
 * else a readable title from normalized `event_type`.
 */
export function tooltipTitleLine(event) {
  const label = (event.label && String(event.label).trim()) ? String(event.label).trim() : '';
  if (label) return label;

  const et = normalizeTimelineEventType(String(event.event_type || '').toLowerCase());
  const spaced = et.replace(/_/g, ' ').trim();
  if (!spaced) return 'Event';
  return capitalizeWords(spaced);
}

export function truncateNotes(notes, maxLen = 80) {
  if (!notes || typeof notes !== 'string') return '';
  const t = notes.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

/** Most recent terminal event (approved/denied) with a vote, for list badge. */
export function getLatestTerminalVoteFromTimeline(timeline) {
  const list = sortEventsByDate(getTimelineEventList(timeline));
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i];
    const et = String(e.event_type || '').toLowerCase();
    if ((et === 'approved' || et === 'denied') && e.vote) {
      return { vote: e.vote, event_type: et };
    }
  }
  return null;
}
